import { requestedWorkspace } from "./utils/workspaceDestination";
// Trigger CI/CD: minor change for deployment
/* eslint-disable no-console, no-unused-vars */
import React, { useState, useEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router";
import DojoPage from "./DojoPage";
import "./App.css";
import { auth, db } from "./firebaseClient";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  signInWithCustomToken,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from "firebase/auth";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { API_ENDPOINTS, API_BASE_URL, PUBLIC_SITE_URL } from "./config";
import EmailVerificationScreen from "./components/EmailVerificationScreen";
import { resendAccountVerification, sendFirebaseVerification, watchEmailVerification } from "./utils/emailVerification";
import { parseJsonSafe } from "./utils/parseJsonSafe";
import { clearSessionPreservingRenderAttempts } from "./utils/viralRenderRecovery";
import {
  ensureActiveWorkspace,
  setActiveWorkspaceId,
  withWorkspaceHeaders,
} from "./utils/workspace";
import { WORKSPACE_ENDPOINTS } from "./config/workspaceApi";
import {
  buildBackendUploadError,
  inferUploadMediaType,
  uploadSourceFileViaBackend,
} from "./utils/sourceUpload";
import PayPalSubscriptionPanel from "./components/PayPalSubscriptionPanel";
import { Sentry } from "./sentryClient";
import TestSentryButton from "./components/TestSentryButton";
import WelcomePage from "./WelcomePage";
import LoginForm from "./LoginForm";
import RegisterForm from "./RegisterForm";
import LogoutConfirmDialog from "./components/LogoutConfirmDialog";
import ForgotPasswordPage from "./ForgotPasswordPage";
import ResetPasswordPage from "./ResetPasswordPage";
import UserDashboard from "./UserDashboard_full";
import ProductAssistance from "./components/ProductAssistance";
import CreditPilotPanel from "./components/CreditPilotPanel";
import { creditFetch } from "./utils/creditPilot";
import { isLocalTestAuthEndpoint, isRuntimeE2EEnabled, getRuntimeE2EToken } from "./utils/mediaAuth";
import { getVerifiedSessionUser, isAdminDashboardPath, shouldShowAdminDashboard } from "./utils/dashboardAccess";

// Static Pages — lazy loaded for bundle splitting
const About = lazy(() => import("./About"));
const Blog = lazy(() => import("./Blog"));
const Careers = lazy(() => import("./Careers"));
const Contact = lazy(() => import("./Contact"));
const Cookies = lazy(() => import("./Cookies"));
const Docs = lazy(() => import("./Docs"));
const Pricing = lazy(() => import("./Pricing"));
const Support = lazy(() => import("./Support"));
const Accessibility = lazy(() => import("./Accessibility"));
const Features = lazy(() => import("./Features"));
const Integrations = lazy(() => import("./Integrations"));
const Changelog = lazy(() => import("./Changelog"));
// CommunityPage removed
const HelpCenter = lazy(() => import("./HelpCenter"));
const ApiDocs = lazy(() => import("./ApiDocs"));
const Partners = lazy(() => import("./Partners"));
const Security = lazy(() => import("./Security"));
const Terms = lazy(() => import("./Terms"));
const Privacy = lazy(() => import("./Privacy"));
const LiveLanding = lazy(() => import("./LiveLanding"));
const LiveWatch = lazy(() => import("./LiveWatch"));
const StreamerDashboard = lazy(() => import("./StreamerDashboard"));
const EngagementMarketplace = lazy(() => import("./EngagementMarketplace"));
const AdminDashboard = lazy(() => import("./AdminDashboard"));
const InternalDemoEditorPage = lazy(() => import("./InternalDemoEditorPage"));

const PageLoader = () => (
  <div
    style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}
  >
    <div style={{ textAlign: "center", color: "#888" }}>Loading...</div>
  </div>
);

function hasRenderableImageMeta(meta = {}) {
  return Boolean(
    meta &&
    (Number(meta.rotate || 0) !== 0 ||
      meta.flipH === true ||
      meta.flipV === true ||
      typeof meta.filter === "string")
  );
}

function hasRenderableVideoTrimMeta(meta = {}) {
  return Boolean(
    meta &&
    (Number(meta.trimStart || 0) > 0 || Number(meta.trimEnd || 0) > 0) &&
    Number(meta.trimEnd || 0) > Number(meta.trimStart || 0)
  );
}

function buildCanvasFilter(filterName) {
  switch (String(filterName || "").toLowerCase()) {
    case "grayscale":
      return "grayscale(1)";
    case "sepia":
      return "sepia(1)";
    case "invert":
      return "invert(1)";
    case "brightness":
      return "brightness(1.14)";
    case "contrast":
      return "contrast(1.26) saturate(1.05)";
    default:
      return "none";
  }
}

async function renderPlatformImageVariant(file, meta, platformName) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error("Could not load the source image for rendering."));
      nextImage.src = objectUrl;
    });

    const radians = ((Number(meta?.rotate || 0) % 360) * Math.PI) / 180;
    const quarterTurn = Math.abs(Math.round(Number(meta?.rotate || 0) / 90)) % 2 === 1;
    const outputWidth = quarterTurn ? image.height : image.width;
    const outputHeight = quarterTurn ? image.width : image.height;
    const canvas = document.createElement("canvas");
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const context = canvas.getContext("2d");

    context.save();
    context.translate(outputWidth / 2, outputHeight / 2);
    context.rotate(radians);
    context.scale(meta?.flipH ? -1 : 1, meta?.flipV ? -1 : 1);
    context.filter = buildCanvasFilter(meta?.filter);
    context.drawImage(image, -image.width / 2, -image.height / 2);
    context.restore();

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        nextBlob => {
          if (nextBlob) resolve(nextBlob);
          else reject(new Error("Failed to render the image variant."));
        },
        file.type || "image/jpeg",
        0.92
      );
    });

    const extension =
      file.name && file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")) : ".jpg";
    return new File([blob], `${platformName}-variant${extension}`, {
      type: blob.type || file.type || "image/jpeg",
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function renderPlatformVideoTrimVariant({ fileUrl, token, meta, platformName }) {
  const response = await fetch(API_ENDPOINTS.MEDIA_RENDER_CLIP, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      fileUrl,
      startTime: Number(meta.trimStart || 0),
      endTime: Number(meta.trimEnd || 0),
    }),
  });

  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.url) {
    throw buildBackendUploadError(
      new Error(result?.message || `Failed to render the trimmed ${platformName} clip.`)
    );
  }

  return result.url;
}

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [logoutInProgress, setLogoutInProgress] = useState(false);
  const [content, setContent] = useState([]);
  const [mySchedules, setMySchedules] = useState([]);
  const [mySchedulesLoading, setMySchedulesLoading] = useState(true);
  const [mySchedulesError, setMySchedulesError] = useState("");
  const [scheduleScope, setScheduleScope] = useState("week");
  const [scheduleCursor, setScheduleCursor] = useState(null);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [userDefaults, setUserDefaults] = useState({
    timezone: "UTC",
    defaultPlatforms: [],
    defaultFrequency: "once",
  });

  // MFA State
  const [mfaResolver, setMfaResolver] = useState(null);
  const [verificationId, setVerificationId] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaError, setMfaError] = useState("");
  // Feature flag: when true (default unless REACT_APP_DISABLE_IMMEDIATE_POSTS is 'false'),
  // disable direct client-side platform posts and rely on backend queued tasks.
  const DISABLE_IMMEDIATE_POSTS =
    process.env.REACT_APP_DISABLE_IMMEDIATE_POSTS === undefined ||
    process.env.REACT_APP_DISABLE_IMMEDIATE_POSTS !== "false";
  const [justLoggedOut, setJustLoggedOut] = useState(false);
  const [termsRequired, setTermsRequired] = useState(false);
  const [requiredTermsVersion, setRequiredTermsVersion] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null); // { userData, token }
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const showAdminDashboard = shouldShowAdminDashboard(user, pathname);

  // Lock page scrolling only while an auth/verification modal is actually
  // visible. Deriving this directly avoids a stale modal-open class surviving
  // the transition from login into the dashboard.
  useEffect(() => {
    const shouldLockScroll =
      showLogin ||
      showRegister ||
      showAdminLogin ||
      showMfaModal ||
      showTermsModal ||
      showLogoutConfirm;
    document.body.classList.toggle("modal-open", shouldLockScroll);

    return () => document.body.classList.remove("modal-open");
  }, [showLogin, showRegister, showAdminLogin, showMfaModal, showTermsModal, showLogoutConfirm]);

  // E2E test auth bypass: when true, skip firebase auth and set test user
  const E2E_AUTH_BYPASS =
    process.env.REACT_APP_E2E_AUTH_BYPASS === "true" && isLocalTestAuthEndpoint();
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (!firebaseUser) {
        // If E2E bypass is active, do not clear localStorage or reset user
        if (isRuntimeE2EEnabled()) {
          return; // keep the E2E bypass user
        }
        setUser(null);
        setPendingVerification(null);
        setIsAdmin(false);
        clearSessionPreservingRenderAttempts();
        setContent([]); // Clear content on logout
        try {
          if (Sentry && typeof Sentry.setUser === "function") Sentry.setUser(null);
        } catch (_) {}
        return;
      }
      if (!firebaseUser.emailVerified) {
        setUser(null);
        setIsAdmin(false);
        localStorage.removeItem("user");
        setPendingVerification(current => current?.uid === firebaseUser.uid ? current : {
          uid: firebaseUser.uid, email: firebaseUser.email,
        });
        return;
      }
      try {
        const token = await firebaseUser.getIdToken(true);
        const userData = await getVerifiedSessionUser(firebaseUser, token);
        if (auth.currentUser?.uid !== firebaseUser.uid) return;
        // Before allowing the app to enter the dashboard, ensure terms are accepted.
        const ok = await ensureTermsAccepted(token, userData, "authState");
        if (!ok) {
          // Block login UI transition until the user accepts. Keep user null for now.
          return;
        }
        if (auth.currentUser?.uid !== firebaseUser.uid) return;
        // Proceed to set user and prefetch content once terms are satisfied.
        setUser({ ...userData, token }); // keep token in memory in React state only
        setPendingVerification(null);
        setShowRegister(false);
        setShowLogin(false);
        try {
          if (Sentry && typeof Sentry.setUser === "function")
            Sentry.setUser({ id: userData.uid, username: userData.email, email: userData.email });
        } catch (_) {}
        setIsAdmin(userData.isAdmin);
        const safeUserForStorage = { ...userData };
        localStorage.setItem("user", JSON.stringify(safeUserForStorage));
        // User signed in (UID suppressed in logs)
        await fetchUserContent(token);
      } catch (error) {
        setUser(null);
        setIsAdmin(false);
        clearSessionPreservingRenderAttempts();
        setContent([]);
      }
    });
    if (window.location.pathname === "/admin-login") {
      setShowAdminLogin(true);
      setShowLogin(false);
      setShowRegister(false);
    }
    return () => {
      try {
        if (unsubscribe && typeof unsubscribe === "function") unsubscribe();
      } catch (_) {}
    };
  }, []);

  useEffect(() => {
    if (!pendingVerification?.uid) return undefined;
    return watchEmailVerification(auth, async firebaseUser => {
      await processLoginSuccess({ user: firebaseUser });
      setPendingVerification(null);
      setShowRegister(false);
      setShowLogin(false);
    });
    // The watcher is bound to one identity and rechecks it after every await.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingVerification?.uid]);

  // If E2E bypass is enabled, set a pre-authorized test user and fetch content
  // Run when E2E_AUTH_BYPASS toggles; fetchUserContent intentionally omitted from deps
  // eslint-disable-next-line
  useEffect(() => {
    if (!E2E_AUTH_BYPASS) return;
    const setTestUser = async () => {
      try {
        const testToken = "e2e-test-token";
        const testUser = {
          uid: "e2e-user",
          email: "e2e@local",
          name: "E2E User",
          role: "user",
          token: testToken,
        };
        setUser(testUser);
        await fetchUserContent(testToken);
      } catch (_) {}
    };
    setTestUser();
  }, [E2E_AUTH_BYPASS]);

  // Runtime E2E bypass via window.__E2E_BYPASS = true set by tests.
  // Run on mount for E2E bypass checks; fetchUserContent intentionally omitted from deps
  // eslint-disable-next-line
  useEffect(() => {
    try {
      if (isRuntimeE2EEnabled()) {
        (async () => {
          try {
            const testToken = getRuntimeE2EToken() || "e2e-test-token";
            // Prefer an existing user object in localStorage (tests may set an admin user there).
            let testUser = null;
            try {
              const raw = localStorage.getItem("user");
              if (raw) {
                testUser = JSON.parse(raw);
                testUser.token = testToken;
              }
            } catch (_) {
              /* ignore parse errors */
            }
            if (!testUser) {
              testUser = {
                uid: "e2e-user",
                email: "e2e@local",
                name: "E2E User",
                role: "user",
                token: testToken,
              };
            }
            setUser(testUser);
            if (testUser.role === "admin" || testUser.isAdmin === true) setIsAdmin(true);
            await fetchUserContent(testToken);
          } catch (_) {}
        })();
      }
    } catch (_) {}
  }, []);

  const fetchUserContent = async (providedToken = null) => {
    try {
      let token = providedToken;
      if (!token) {
        const currentUser = auth.currentUser;
        if (currentUser) {
          token = await currentUser.getIdToken(true);
        } else if (user && user.token) {
          token = user.token;
        } else {
          setMySchedulesLoading(false);
          setMySchedulesError("Sign in to load your schedule.");
          return;
        }
      }
      // The owned publishing queue must not wait for workspace/content loading.
      // Its authenticated endpoint handles its own errors and authorization.
      void fetchMySchedules(token);
      const workspaceId = await ensureActiveWorkspace(WORKSPACE_ENDPOINTS.CURRENT, token);
      const res = await fetch(`${API_ENDPOINTS.MY_CONTENT}?includeStats=0`, {
        method: "GET",
        headers: withWorkspaceHeaders(
          {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          workspaceId
        ),
        mode: "cors",
      });
      if (!res.ok) {
        // Try to read response body for error details
        let body = null;
        try {
          const parsed = await parseJsonSafe(res);
          body = parsed.json || null;
        } catch (_) {
          body = null;
        }
        if (res.status === 401 && auth.currentUser) {
          const freshToken = await auth.currentUser.getIdToken(true);
          return fetchUserContent(freshToken);
        }
        if (res.status === 403 && body && body.error === "terms_not_accepted") {
          // Show a full-screen modal rather than an in-dashboard banner
          setTermsRequired(true);
          setRequiredTermsVersion(body.requiredVersion || null);
          setShowTermsModal(true);
          setMySchedulesLoading(false);
          setMySchedulesError("Accept the updated terms to load your schedule.");
          return;
        }
        if (res.status === 403) {
          // Quietly suppress generic 403s during background fetch to avoid console noise
          setMySchedulesLoading(false);
          setMySchedulesError("Schedule could not be loaded. Try again.");
          return;
        }
        setMySchedulesLoading(false);
        setMySchedulesError("Schedule could not be loaded. Try again.");
        return;
      }
      const parsed = await parseJsonSafe(res);
      const data = parsed.json || null;
      setContent((data && data.content) || []);
    } catch (error) {
      setMySchedulesLoading(false);
      setMySchedulesError("Schedule could not be loaded. Try again.");
    }
  };

  const fetchMySchedules = async (providedToken = null, options = {}) => {
    setMySchedulesLoading(true);
    setSchedulesLoading(true);
    setMySchedulesError("");
    try {
      let token = providedToken;
      if (!token) {
        const currentUser = auth.currentUser;
        if (currentUser) token = await currentUser.getIdToken(true);
        else if (user && user.token) token = user.token;
        else throw new Error("Sign in to load your schedule.");
      }
      const scope = options.scope || scheduleScope;
      const query = new URLSearchParams({ limit: "250" });
      if (scope === "week") {
        const now = Date.now();
        query.set("from", new Date(now).toISOString());
        query.set("until", new Date(now + 7 * 86400000).toISOString());
      }
      if (options.cursor) query.set("cursor", options.cursor);
      const res = await fetch(`${API_ENDPOINTS.MY_SCHEDULES}?${query}`, {
        method: "GET",
        headers: withWorkspaceHeaders({
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        }),
      });
      if (!res.ok) throw new Error("Schedule could not be loaded. Try again.");
      const parsed = await parseJsonSafe(res);
      const data = parsed.json || null;
      if (!Array.isArray(data?.schedules)) throw new Error("Schedule response was incomplete.");
      const rows = data.schedules;
      setMySchedules(previous =>
        options.cursor
          ? [...previous, ...rows.filter(row => !previous.some(old => old.id === row.id))]
          : rows
      );
      setScheduleCursor(data?.meta?.nextCursor || null);
      setScheduleScope(scope);
    } catch (error) {
      setMySchedulesError(error.message || "Schedule could not be loaded.");
    } finally {
      setMySchedulesLoading(false);
      setSchedulesLoading(false);
    }
  };

  // Expose a refresh handler to child components that perform schedule actions
  const refreshSchedules = async () => {
    await fetchMySchedules();
  };

  const fetchAnalytics = async (providedToken = null) => {
    let token = providedToken;
    if (!token) {
      const currentUser = auth.currentUser;
      if (currentUser) {
        token = await currentUser.getIdToken(true);
      } else if (user && user.token) {
        token = user.token;
      } else {
        return;
      }
    }
    if (!user || !user.role) {
      setIsAdmin(false);
      return;
    }
    const isAdminUser = user.role === "admin" || user.isAdmin === true;
    if (!isAdminUser) {
      return;
    }
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_ANALYTICS, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      const parsed = await parseJsonSafe(res);
      if (!parsed.ok) {
        const body = parsed.json || null;
        if (res.status === 401 && auth.currentUser) {
          const freshToken = await auth.currentUser.getIdToken(true);
          return fetchAnalytics(freshToken);
        }
        if (res.status === 403 && body && body.error === "terms_not_accepted") {
          setTermsRequired(true);
          setRequiredTermsVersion(body.requiredVersion || null);
          return;
        }
        setIsAdmin(false);
        return;
      } else {
        const data = parsed.json;
        setAnalytics(data);
      }
    } catch (error) {}
  };

  const processLoginSuccess = async userCredential => {
    const { user: firebaseUser } = userCredential;
    const idToken = await firebaseUser.getIdToken();
    const res = await fetch(API_ENDPOINTS.LOGIN, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ idToken }),
    });
    if (res.ok) {
      const parsed = await parseJsonSafe(res);
      const data = parsed.json || null;
      if (data.customToken) {
        const customUserCredential = await signInWithCustomToken(auth, data.customToken);
        const customIdToken = await customUserCredential.user.getIdToken();
        try {
          if (localStorage.getItem("tosAgreed") === "true") {
            const url = `${API_BASE_URL}/api/users/me/accept-terms`;
            await fetch(url, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${customIdToken}`,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({}),
            }).catch(() => {});
            localStorage.removeItem("tosAgreed");
          }
        } catch (_) {}
        const userData = { ...data.user, token: customIdToken };
        const ok = await ensureTermsAccepted(customIdToken, userData, "login");
        if (ok) handleLogin(userData);
      } else {
        try {
          if (localStorage.getItem("tosAgreed") === "true") {
            const url = `${API_BASE_URL}/api/users/me/accept-terms`;
            await fetch(url, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${idToken}`,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({}),
            }).catch(() => {});
            localStorage.removeItem("tosAgreed");
          }
        } catch (_) {}
        const userData = { ...data.user, token: idToken };
        const ok = await ensureTermsAccepted(idToken, userData, "login");
        if (ok) handleLogin(userData);
      }
    } else {
      const parsedErr = await parseJsonSafe(res);
      const errorBody = parsedErr.json || null;
      const loginError = new Error(errorBody?.message || "Login failed");
      loginError.code =
        errorBody?.error === "email_not_verified"
          ? "auth/email-not-verified"
          : errorBody?.error || "auth/login-failed";
      throw loginError;
    }
  };

  const sendMfaCode = async () => {
    if (!mfaResolver) return;
    setMfaError("");
    const hints = mfaResolver.hints;
    const phoneInfoOptions = {
      multiFactorHint: hints[0],
      session: mfaResolver.session,
    };
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "mfa-recaptcha-container", {
        size: "invisible",
      });
    }
    try {
      const vId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        window.recaptchaVerifier
      );
      setVerificationId(vId);
    } catch (e) {
      setMfaError(e.message);
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    }
  };

  const cancelMfa = () => {
    setShowMfaModal(false);
    setMfaResolver(null);
    setVerificationId(null);
    setMfaCode("");
    setMfaError("");
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (e) {
        // ignore
      }
      window.recaptchaVerifier = null;
    }
  };

  const verifyMfaCode = async () => {
    setMfaError("");
    try {
      const cred = PhoneAuthProvider.credential(verificationId, mfaCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      const userCredential = await mfaResolver.resolveSignIn(multiFactorAssertion);
      await processLoginSuccess(userCredential);
      setShowMfaModal(false);
      setMfaResolver(null);
      setVerificationId(null);
      setMfaCode("");
    } catch (e) {
      setMfaError(e.message);
    }
  };

  const loginUser = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await processLoginSuccess(userCredential);
    } catch (error) {
      if (error.code === "auth/multi-factor-auth-required") {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        setShowMfaModal(true);
        return;
      }
      throw error;
    }
  };

  // Ensure terms are accepted; if not, show modal and defer login
  const ensureTermsAccepted = async (token, userData, source) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.MY_CONTENT}?includeStats=0`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      if (res.ok) return true; // already accepted
      let body = null;
      try {
        const parsed = await parseJsonSafe(res);
        body = parsed.json || null;
      } catch (_) {}
      if (res.status === 403 && body && body.error === "terms_not_accepted") {
        setRequiredTermsVersion(body.requiredVersion || null);
        setTermsRequired(true);
        setPendingLogin({ userData, token });
        setShowTermsModal(true);
        return false;
      }
      return true; // treat other failures as non-blocking for login
    } catch (_) {
      return true;
    }
  };

  const registerUser = async (name, email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      await updateProfile(firebaseUser, { displayName: name });

      const idToken = await firebaseUser.getIdToken();
      const res = await fetch(API_ENDPOINTS.REGISTER, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ name, email, uid: firebaseUser.uid, idToken }),
      });

      const responseBody = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          responseBody.error || responseBody.message || "Registration failed on server."
        );
      }

      let verificationEmailSent = responseBody.verificationEmailSent === true;
      let verificationDelivery = responseBody.verificationProvider || null;
      let verificationMessage = "";
      let verificationRetryAfterSeconds = verificationEmailSent ? 60 : 0;
      if (!verificationEmailSent) {
        try {
          if (responseBody.verificationErrorCode === "auth/too-many-requests") {
            throw Object.assign(new Error("Verification email is temporarily limited. Please wait before trying again."), { retryAfterSeconds: 120 });
          }
          await sendFirebaseVerification(firebaseUser);
          verificationEmailSent = true;
          verificationDelivery = "firebase";
          verificationRetryAfterSeconds = 60;
        } catch (verificationError) {
          verificationMessage = verificationError.message;
          verificationRetryAfterSeconds = verificationError.retryAfterSeconds || 30;
        }
      }

      // Keep the unverified Firebase session only so the resend button can use
      // Firebase's verified delivery path. onAuthStateChanged still blocks the
      // dashboard until Firebase reports emailVerified=true.

      const result = {
        email,
        verificationEmailSent,
        verificationDelivery,
        verificationMessage,
        verificationRetryAfterSeconds,
      };
      setPendingVerification(current => current?.uid === firebaseUser.uid ? {
        ...current, sent: verificationEmailSent, message: verificationMessage,
        retryAfterSeconds: verificationRetryAfterSeconds,
      } : current);
      return result;
    } catch (error) {
      await signOut(auth); // Ensure signed out on error
      alert("Registration failed: " + (error.message || "Unknown error"));
      throw error;
    }
  };

  const resendVerificationEmail = async email => {
    return resendAccountVerification(email, auth.currentUser);
  };

  const handleLogin = async userData => {
    try {
      clearSessionPreservingRenderAttempts();
      if (!userData || !userData.role) {
        setShowLogin(true);
        setUser(null);
        setIsAdmin(false);
        return;
      }
      if (!userData.token) {
        return;
      }
      const forceAdmin = userData.role === "admin" || userData.isAdmin === true;
      // Use the current verified login; never carry privileges from a prior session.
      const updatedUserData = {
        ...userData,
        role: forceAdmin ? "admin" : userData.role,
        isAdmin: forceAdmin,
      };
      // Persist only non-sensitive metadata. Remove token before storing.
      const { token: _token, ...safeUpdated } = updatedUserData;
      localStorage.setItem("user", JSON.stringify(safeUpdated));
      setUser(updatedUserData);
      setIsAdmin(forceAdmin);
      setShowLogin(false);
      // ...existing code... (Firestore update logic removed)
      if (forceAdmin && isAdminDashboardPath(pathname)) {
        navigate("/admin-dashboard");
      } else {
        await fetchUserContent(userData.token);
        const destination = requestedWorkspace(search);
        navigate(destination ? `/?tab=${destination}` : "/");
      }
    } catch (error) {
      handleLogout();
    }
  };

  const handleRegister = userData => {
    setUser(userData);
    setShowRegister(false);
  };

  const loadUserDefaults = async providedToken => {
    try {
      let token = providedToken;
      if (!token) {
        const currentUser = auth.currentUser;
        if (currentUser) token = await currentUser.getIdToken(true);
        else if (user && user.token) token = user.token;
        else return;
      }

      const res = await fetch(API_ENDPOINTS.USERS_ME, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (!res.ok) return;

      const data = await res.json().catch(() => ({}));
      const u = data && data.user ? data.user : {};
      const sched = u.schedulingDefaults || {};

      setUserDefaults({
        timezone: u.timezone || "UTC",
        schedulingDefaults: sched,
        defaultPlatforms: sched.platforms || [],
        defaultFrequency: sched.frequency || "once",
        paypalEmail: u.paypalEmail || "",
      });
    } catch (_) {}
  };

  const loadSubscriptionProfile = async providedToken => {
    try {
      let token = providedToken;
      if (!token) {
        const currentUser = auth.currentUser;
        if (currentUser) token = await currentUser.getIdToken(true);
        else return;
      }

      const response = await fetch(API_ENDPOINTS.USERS_PROFILE, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (!response.ok) return;

      const profile = await response.json().catch(() => ({}));
      if (!profile || profile.success === false) return;
      setUser(current => {
        if (!current) return current;
        return {
          ...current,
          ...profile,
          uid: current.uid,
          email: current.email,
          role: current.role,
          isAdmin: current.isAdmin,
          user: {
            ...(current.user || {}),
            ...(profile.user || {}),
          },
          token: current.token,
        };
      });
    } catch (_) {
      // Profile enrichment is non-blocking; Firebase authentication remains authoritative.
    }
  };

  useEffect(() => {
    if (!user || showAdminDashboard) return;
    loadUserDefaults().catch(() => {});
    loadSubscriptionProfile().catch(() => {});
  }, [user?.uid, user?.role, user?.isAdmin, showAdminDashboard]);

  useEffect(() => {
    if (showAdminDashboard) fetchAnalytics();
    // Fetch only after verified state has selected an explicit admin entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdminDashboard, user?.uid]);

  // Save user defaults (timezone, default platforms, frequency, paypalEmail)
  const saveUserDefaults = async ({
    timezone,
    defaultPlatforms,
    defaultFrequency,
    paypalEmail,
  }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");
      const token = await currentUser.getIdToken(true);
      const res = await fetch(API_ENDPOINTS.USERS_ME, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          timezone,
          defaultPlatforms,
          defaultFrequency,
          paypalEmail,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || "Failed to save defaults");
      }
      const data = await res.json();
      const u = data && data.user ? data.user : {};
      const sched = u.schedulingDefaults || {};
      setUserDefaults({
        timezone: u.timezone || timezone || "UTC",
        schedulingDefaults: sched,
        defaultPlatforms: sched.platforms || defaultPlatforms || [],
        defaultFrequency: sched.frequency || defaultFrequency || "once",
        paypalEmail: u.paypalEmail || paypalEmail || "",
      });
      return true;
    } catch (e) {
      alert(e.message || "Could not save settings");
      return false;
    }
  };

  const handleLogout = async () => {
    try {
      // User logged out
      await signOut(auth);
      setUser(null);
      setContent([]);
      setIsAdmin(false);
      setShowLogin(false);
      setShowRegister(false);
      setShowAdminLogin(false);
      clearSessionPreservingRenderAttempts();
      setJustLoggedOut(true);
      setTermsRequired(false);
      setRequiredTermsVersion(null);
      return true;
    } catch (error) {
      console.error("Logout error:", error);
      return false;
    }
  };

  const confirmLogout = async () => {
    if (logoutInProgress) return;
    setLogoutInProgress(true);
    const signedOut = await handleLogout();
    setLogoutInProgress(false);
    if (signedOut) setShowLogoutConfirm(false);
  };

  // Accept Terms action: posts acceptance and continues pending login if any
  const acceptTerms = async () => {
    try {
      // Prefer the token from a pending login (pre-dashboard), else current user
      let token = pendingLogin?.token || null;
      if (!token) {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        token = await currentUser.getIdToken(true);
      }
      const url = `${API_BASE_URL}/api/users/me/accept-terms`;
      const payload = requiredTermsVersion ? { acceptedTermsVersion: requiredTermsVersion } : {};
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert("Failed to accept terms: " + (body.error || res.status));
        return;
      }
      setTermsRequired(false);
      setRequiredTermsVersion(null);
      setShowTermsModal(false);
      if (pendingLogin && pendingLogin.userData) {
        // Complete the deferred login now that terms are accepted
        const u = pendingLogin.userData;
        setPendingLogin(null);
        handleLogin(u);
      } else {
        await fetchUserContent();
        if (isAdmin) await fetchAnalytics();
      }
    } catch (e) {
      alert("Could not accept terms. Please try again.");
    }
  };

  // Redirect after logout
  React.useEffect(() => {
    if (justLoggedOut && !user) {
      navigate("/");
      setJustLoggedOut(false);
    }
  }, [justLoggedOut, user, navigate]);

  // Content upload handler (with file and platforms)
  const handleContentUpload = async params => {
    const blockingErrorCodes = new Set([
      "TIER_LIMIT_EXCEEDED",
      "PLATFORM_LIMIT_EXCEEDED",
      "UPLOAD_CAP_EXCEEDED",
      "PROMOTION_TASK_QUOTA_EXCEEDED",
    ]);
    const buildStructuredUploadError = (result, fallbackMessage, httpStatus) => {
      const serverErr =
        (result && (result.error || result.message || result.text)) || fallbackMessage;
      const enrichedError = new Error(serverErr || "Upload failed");
      if (result && typeof result === "object") {
        enrichedError.code = result.code;
        enrichedError.context = result.context || null;
        enrichedError.upgradeRequired = result.upgrade_required === true;
      }
      if (httpStatus) enrichedError.httpStatus = httpStatus;
      return enrichedError;
    };
    const cleanupUploadedFiles = async () => Promise.resolve();
    // If called without params (e.g. from ClipStudioPanel refresh), just reload content
    if (!params) {
      console.log("[App] handleContentUpload called without params -> refreshing content");
      let token = null;
      try {
        const current = auth && auth.currentUser;
        if (current) token = await current.getIdToken(true);
      } catch (_) {}
      if (token) await fetchUserContent(token);
      return;
    }

    console.log("[E2E] handleContentUpload called with params:", {
      isDryRun: params.isDryRun,
      platforms: params.platforms || params.target_platforms,
    });
    try {
      // Destructure all possible fields from params
      const {
        file,
        platforms,
        title,
        description,
        type,
        schedule,
        isDryRun,
        trimStart,
        trimEnd,
        template,
        rotate,
        flipH,
        flipV,
      } = params;
      const requestedPlatforms =
        platforms && platforms.length
          ? platforms
          : userDefaults.defaultPlatforms || ["youtube", "tiktok", "instagram"];
      const scheduledPromotionTime = (() => {
        const raw =
          params.scheduled_promotion_time ||
          schedule?.date ||
          schedule?.when ||
          schedule?.scheduled_promotion_time;
        if (typeof raw !== "string") return undefined;
        const normalized = raw.trim();
        if (!normalized) return undefined;
        const parsed = Date.parse(normalized);
        return Number.isFinite(parsed) ? normalized : undefined;
      })();
      const platformScheduleTimes = (() => {
        const rawMap =
          params.platform_schedule_times ||
          schedule?.platformScheduleTimes ||
          schedule?.platform_schedule_times;
        if (!rawMap || typeof rawMap !== "object" || Array.isArray(rawMap)) return undefined;

        const normalizedMap = Object.entries(rawMap).reduce((acc, [platform, value]) => {
          if (typeof platform !== "string" || typeof value !== "string") return acc;
          const trimmedPlatform = platform.trim();
          const trimmedValue = value.trim();
          const parsed = Date.parse(trimmedValue);
          if (trimmedPlatform && Number.isFinite(parsed)) {
            acc[trimmedPlatform] = new Date(parsed).toISOString();
          }
          return acc;
        }, {});

        return Object.keys(normalizedMap).length > 0 ? normalizedMap : undefined;
      })();
      // Use Firebase auth token when available; fall back to app user token or runtime E2E test token
      let token = null;
      try {
        const current = auth && auth.currentUser;
        if (current) token = await current.getIdToken(true);
      } catch (_) {
        token = null;
      }
      if (!token && user && user.token) token = user.token;
      if (
        !token &&
        typeof window !== "undefined" &&
        isRuntimeE2EEnabled() &&
        window.__E2E_TEST_TOKEN
      )
        token = window.__E2E_TEST_TOKEN;
      if (!token) {
        throw new Error("Authentication token missing for content upload request");
      }
      if (!isDryRun) {
        const readinessResponse = await fetch(API_ENDPOINTS.CONTENT_UPLOAD_READINESS, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            target_platforms: requestedPlatforms,
            scheduled_promotion_time: scheduledPromotionTime,
            platform_schedule_times: platformScheduleTimes,
          }),
        });
        let readinessResult = null;
        try {
          readinessResult = await readinessResponse.json();
        } catch (_) {
          readinessResult = null;
        }
        if (!readinessResponse.ok) {
          throw buildStructuredUploadError(
            readinessResult,
            `HTTP ${readinessResponse.status}`,
            readinessResponse.status
          );
        }
        if (readinessResult?.readiness?.allowed === false) {
          throw buildStructuredUploadError(
            {
              error: readinessResult.readiness.message,
              code: readinessResult.readiness.code,
              context: readinessResult.readiness.context,
              upgrade_required: readinessResult.readiness.context?.upgrade_required === true,
            },
            readinessResult.readiness.message,
            403
          );
        }
      }
      let finalUrl = "";
      // If this is a preview/dry-run, do NOT upload the file to storage.
      // Use a `preview://` URL so preview pipelines and workers treat it as a local preview token.
      if (isDryRun && file) {
        finalUrl = `preview://${file.name}`;
      } else if (!isDryRun && !file && params.url) {
        // If the caller already uploaded the file (some upload forms do client-side storage
        // upload) prefer the provided `url` instead of re-uploading.
        finalUrl = params.url;
      } else {
        // Only upload file for real submissions (not dry-run)
        if ((type === "video" || type === "image" || type === "audio") && file) {
          if (file.size < 100) {
            throw new Error("File too small. Please check the file.");
          }
          const uploadResult = await uploadSourceFileViaBackend({
            file,
            token,
            mediaType: inferUploadMediaType(file, type || "video"),
            fileName: file.name,
          }).catch(error => {
            throw buildBackendUploadError(error);
          });

          if ((uploadResult.size || 0) < 100) {
            throw new Error(
              `Upload failed: File corrupted (size: ${uploadResult.size || 0} bytes).`
            );
          }

          finalUrl = uploadResult.url;
        }
      }

      // 2. Build/Upload Platform-Specific Media Variants
      const platformFiles = { ...(params.platform_files || {}) };
      const platformOptionsObj = { ...(params.platformOptions || params.platform_options || {}) };
      const platformRenderWarnings = [];

      if (!isDryRun && finalUrl && file && Object.keys(platformOptionsObj).length > 0) {
        for (const [platformName, options] of Object.entries(platformOptionsObj)) {
          const meta = options?.meta;
          if (!meta || platformFiles[platformName]) continue;

          try {
            if (type === "image" && file instanceof File && hasRenderableImageMeta(meta)) {
              platformFiles[platformName] = await renderPlatformImageVariant(
                file,
                meta,
                platformName
              );
            } else if (type === "video" && hasRenderableVideoTrimMeta(meta)) {
              const renderedUrl = await renderPlatformVideoTrimVariant({
                fileUrl: finalUrl,
                token,
                meta,
                platformName,
              });
              if (!platformOptionsObj[platformName]) platformOptionsObj[platformName] = {};
              platformOptionsObj[platformName].media_url = renderedUrl;
              platformOptionsObj[platformName].renderPipeline = "trimmed_clip";
            } else if (
              type === "video" &&
              (meta.rotate || meta.flipH || meta.flipV || meta.filter)
            ) {
              platformRenderWarnings.push(
                `${platformName}: save a platform-specific edited file to apply rotate/flip/filter on video.`
              );
            }
          } catch (error) {
            console.error(`[App] Failed to build platform render for ${platformName}`, error);
            platformRenderWarnings.push(
              `${platformName}: variant render failed, so this publish will fall back to the master media.`
            );
          }
        }
      }

      if (platformFiles && Object.keys(platformFiles).length > 0 && !isDryRun) {
        for (const [pName, pFile] of Object.entries(platformFiles)) {
          if (pFile && pFile instanceof Blob) {
            try {
              const uploadResult = await uploadSourceFileViaBackend({
                file: pFile,
                token,
                mediaType: inferUploadMediaType(pFile, type || "video"),
                fileName: `${pName}_${pFile.name || "variant"}`,
              });
              const pUrl = uploadResult.url;
              if (!platformOptionsObj[pName]) platformOptionsObj[pName] = {};
              platformOptionsObj[pName].media_url = pUrl;
            } catch (err) {
              console.error(`[App] Failed to upload file for ${pName}`, err);
              platformRenderWarnings.push(
                `${pName}: variant upload failed, so this publish will use the master media.`
              );
            }
          }
        }
      }

      if (platformRenderWarnings.length > 0) {
        console.warn("[App] Platform render warnings:", platformRenderWarnings);
      }

      params.platform_options = platformOptionsObj;
      if (params.platformOptions) params.platformOptions = platformOptionsObj;

      const schedule_hint = {
        ...schedule,
        frequency: schedule?.frequency || userDefaults.defaultFrequency || "once",
        timezone: schedule?.timezone || userDefaults.timezone || "UTC",
        mode: scheduledPromotionTime || platformScheduleTimes ? "queued_new_upload" : "publish_now",
        platform_schedule_times: platformScheduleTimes,
      };
      // Defensive: ensure non-empty URL for real uploads (avoid sending empty "url" to server)
      if (!isDryRun && (!finalUrl || String(finalUrl).trim() === "")) {
        alert("Upload failed: missing file URL. Please retry the upload.");
        return { ok: false, error: "missing_url" };
      }
      // Extract monetization settings from platform options for Revenue Engine
      const pOps = params.platformOptions || params.platform_options || {};
      const tiktokOps = pOps.tiktok || {};
      const youtubeOps = pOps.youtube || {};
      const instaOps = pOps.instagram || {};

      const monetization_settings = {
        niche: tiktokOps.niche || "general",
        is_sponsored: !!(
          tiktokOps.brandedContent ||
          tiktokOps.commercial?.brandedContent ||
          youtubeOps.paidPromotion ||
          instaOps.isPaidPartnership
        ),
        brand_name: tiktokOps.brandName || instaOps.sponsorUser || "",
        product_link: tiktokOps.product_link || "",
        commercial_rights: !!tiktokOps.commercialContent,
      };
      const firstPlatformTitle = Object.values(pOps)
        .map(options => options?.title)
        .find(value => String(value || "").trim());
      const firstPlatformDescription = Object.values(pOps)
        .map(
          options =>
            options?.description || options?.caption || options?.message || options?.commentary
        )
        .find(value => String(value || "").trim());
      const normalizedTitle = String(title || firstPlatformTitle || "").trim() || "Untitled Post";
      const normalizedDescription = String(description || firstPlatformDescription || "").trim();

      const payload = {
        isDryRun: !!isDryRun,
        title: normalizedTitle,
        type: type || "video",
        url: finalUrl,
        description: normalizedDescription,
        monetization_settings,
        // Pass through Viral/Bounty/Quality fields from ContentUploadForm
        bounty: params.bounty,
        viral_boost: params.viral_boost,
        variants: Array.isArray(params.variants) ? params.variants : undefined,
        variant_strategy:
          Array.isArray(params.variants) && params.variants.length > 0 ? "rotation" : undefined,
        quality_enhanced: params.quality_enhanced,
        enhance_quality: params.quality_enhanced, // alias for backend
        custom_hashtags: params.custom_hashtags,
        growth_guarantee: params.growth_guarantee,

        target_platforms:
          platforms && platforms.length
            ? platforms
            : userDefaults.defaultPlatforms || ["youtube", "tiktok", "instagram"],
        platform_options: pOps,
        scheduled_promotion_time: scheduledPromotionTime,
        platform_schedule_times: platformScheduleTimes,
        promotion_frequency: schedule_hint.frequency,
        schedule_hint,
        meta: {
          ...(params.meta || {}),
          ...(typeof trimStart !== "undefined" ? { trimStart } : {}),
          ...(typeof trimEnd !== "undefined" ? { trimEnd } : {}),
          ...(typeof rotate !== "undefined" ? { rotate } : {}),
          ...(typeof flipH !== "undefined" ? { flipH } : {}),
          ...(typeof flipV !== "undefined" ? { flipV } : {}),
          ...(template ? { template } : {}),
        },
      };
      console.log(
        "[E2E] handleContentUpload: calling API",
        API_ENDPOINTS.CONTENT_UPLOAD,
        "payload:",
        payload,
        "token?",
        Boolean(token)
      );
      const res = await creditFetch(API_ENDPOINTS.CONTENT_UPLOAD, {
        method: "POST",
        headers: withWorkspaceHeaders({
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        }),
        body: JSON.stringify(payload),
      });
      // Read response body safely so we can show any server-side validation error
      let result = null;
      try {
        result = await res.json();
      } catch (e) {
        // If response is not JSON, try to read as text
        try {
          const txt = await res.text();
          result = { text: txt };
        } catch (_) {
          result = null;
        }
      }
      if (!res.ok) {
        throw buildStructuredUploadError(result, `HTTP ${res.status}`, res.status);
      }
      if (isDryRun) {
        return result;
      }

      // If server marked the content as pending approval, do not attempt any immediate posts.
      if (result && result.content && result.content.status === "pending_approval") {
        await fetchUserContent(token);
        alert(
          "Content uploaded and queued for promotion. It will be published to selected platforms after approval."
        );
        return result;
      }
      try {
        const chosen = Array.isArray(platforms) ? platforms : [];
        // Require explicit user request to perform immediate platform posts.
        const shouldImmediatePost = params?.immediate_post === true && !DISABLE_IMMEDIATE_POSTS;
        const postYouTube = async () => {
          if (!shouldImmediatePost) {
            console.log("[Upload] Immediate YouTube post not requested or disabled");
            return;
          }
          if (!chosen.includes("youtube")) return;
          try {
            // Ensure contentId and fileUrl are sent as required by backend
            // Try all possible keys for contentId from upload response
            // Backend returns { contentId: '...' } on successful upload
            // Try to extract contentId from backend response structure
            let contentId = result?.contentId || result?.promotion_schedule?.contentId;
            // Fallback: try to get from Firestore content list if available
            if (!contentId && Array.isArray(content)) {
              // Try to find a matching content item with the same url
              const match = content.find(c => c.url === payload.url);
              if (match && match.id) contentId = match.id;
              else {
                const last = content[content.length - 1];
                if (last && last.id) contentId = last.id;
              }
            }
            console.log(
              "YouTube upload: contentId",
              contentId,
              "fileUrl",
              payload.url,
              "result",
              result
            );
            const fileUrl = payload.url;
            if (!contentId || !fileUrl) {
              console.warn("Missing contentId or fileUrl for YouTube upload");
              return;
            }
            const r = await fetch(API_ENDPOINTS.YOUTUBE_UPLOAD, {
              method: "POST",
              headers: withWorkspaceHeaders({
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                Accept: "application/json",
              }),
              body: JSON.stringify({
                contentId,
                videoUrl: payload.url,
                title: normalizedTitle,
                description: description || "",
                shortsMode: payload.platform_options?.youtube?.shortsMode,
              }),
            });
            if (!r.ok) console.warn("YouTube upload failed");
          } catch (_) {}
        };
        const getFacebookStatus = async () => {
          const s = await fetch(API_ENDPOINTS.FACEBOOK_STATUS, {
            headers: withWorkspaceHeaders({
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            }),
          });
          if (!s.ok) return null;
          return s.json();
        };
        const postFacebook = async () => {
          if (!chosen.includes("facebook")) return;
          try {
            const st = await getFacebookStatus();
            const pageId = st?.pages?.[0]?.id;
            if (!pageId) return;
            const body = {
              pageId,
              content: {
                type: type || "video",
                url: payload.url,
                title: normalizedTitle,
                description: description || "",
              },
            };
            const r = await fetch(API_ENDPOINTS.FACEBOOK_UPLOAD, {
              method: "POST",
              headers: withWorkspaceHeaders({
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                Accept: "application/json",
              }),
              body: JSON.stringify(body),
            });
            if (!r.ok) console.warn("Facebook upload failed");
          } catch (_) {}
        };
        const postInstagram = async () => {
          if (!chosen.includes("instagram")) return;
          try {
            const st = await getFacebookStatus();
            const pageId = st?.pages?.[0]?.id;
            if (!pageId) return;
            const mediaType = (type || "video").toLowerCase();
            const r = await fetch(API_ENDPOINTS.INSTAGRAM_UPLOAD, {
              method: "POST",
              headers: withWorkspaceHeaders({
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                Accept: "application/json",
              }),
              body: JSON.stringify({
                pageId,
                mediaUrl: payload.url,
                caption: `${title || ""}\n${description || ""}`.trim(),
                mediaType,
              }),
            });
            if (!r.ok) console.warn("Instagram upload failed");
          } catch (_) {}
        };
        // Only run immediate posts when explicitly requested by the user (params.immediate_post)
        if (params?.immediate_post === true && !DISABLE_IMMEDIATE_POSTS) {
          await postYouTube();
          await postFacebook();
          await postInstagram();
        } else {
          console.log(
            "[Upload] Immediate platform posts not requested or disabled; posts will be processed by backend queued tasks"
          );
        }
      } catch (e) {
        console.warn("Auto-post skipped or partial:", e?.message);
      }
      await fetchUserContent(token);
      alert(
        "Content uploaded and queued for promotion. It will be published to selected platforms after approval."
      );
      return result;
    } catch (error) {
      if (!params?.isDryRun && blockingErrorCodes.has(error?.code)) {
        await cleanupUploadedFiles();
      }
      console.error("handleContentUpload error:", error);
      if (!blockingErrorCodes.has(error?.code)) {
        alert("Error uploading content: " + error.message);
      }
      throw error;
    }
  };

  // No manual hash/routing logic needed with react-router-dom
  // Existing logic for routes replaced by <Routes> block below

  return (
    <div className="App">
      <CreditPilotPanel />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Static Content Pages */}
          <Route path="/about" element={<About />} />
          <Route path="/blog/*" element={<Blog />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/docs/*" element={<Docs />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/support" element={<Support />} />
          <Route path="/accessibility" element={<Accessibility />} />

          {/* GAMIFICATION / DOJO */}
          <Route path="/dojo/trend-analyzer" element={<DojoPage />} />

          <Route path="/features" element={<Features />} />
          <Route path="/integrations" element={<Integrations />} />
          {["analytics", "metrics"].map(path => (
            <Route key={path} path={`/${path}/*`} element={<Navigate replace to="/?tab=schedules" />} />
          ))}
          {["smart-promo", "smart_promo", "smartpromo", "idea-to-video", "idea_to_video", "idea-video", "idea_video"].map(path => (
            <Route key={path} path={`/${path}/*`} element={<Navigate replace to="/?tab=viral_clip_studio" />} />
          ))}
          <Route path="/changelog" element={<Changelog />} />
          {/* CommunityPage removed */}
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/api-docs" element={<ApiDocs />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/security" element={<Security />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Live Streaming Pages */}
          <Route path="/live" element={<LiveLanding />} />
          <Route path="/live/watch" element={<LiveWatch />} />
          <Route path="/streamer" element={<StreamerDashboard />} />
          <Route path="/marketplace" element={<EngagementMarketplace />} />
          <Route path="/internal/demo-editor" element={<InternalDemoEditorPage />} />

          {/* Main Application Logic (Welcome / Auth / Dashboard) */}
          <Route
            path="*"
            element={
              <>
                {/* Terms Modal */}
                {showTermsModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      background: "rgba(0,0,0,0.5)",
                      zIndex: 10000,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: 16,
                        boxShadow: "0 12px 36px rgba(0,0,0,0.2)",
                        padding: "24px 22px",
                        maxWidth: 560,
                        width: "90%",
                      }}
                    >
                      <h3 style={{ marginTop: 0, marginBottom: 8 }}>Accept Terms of Service</h3>
                      <p style={{ marginTop: 0, color: "#444" }}>
                        Please accept the latest Terms of Service
                        {requiredTermsVersion ? ` (${requiredTermsVersion})` : ""} to continue.
                      </p>
                      <div
                        style={{ display: "flex", gap: 12, marginTop: 16, alignItems: "center" }}
                      >
                        <button
                          onClick={acceptTerms}
                          style={{
                            background: "#111827",
                            color: "#fff",
                            border: "none",
                            padding: "10px 16px",
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                        >
                          Accept and Continue
                        </button>
                        <a href={`${PUBLIC_SITE_URL}/terms`} target="_blank" rel="noreferrer">
                          View Terms
                        </a>
                      </div>
                    </div>
                  </div>
                )}
                {/* If no user, show welcome/login page */}
                {!user ? (
                  <>
                    {pendingVerification ? (
                      <EmailVerificationScreen {...pendingVerification} onResend={resendVerificationEmail} />
                    ) : !showLogin && !showRegister && (
                      <WelcomePage
                        onGetStarted={() => setShowRegister(true)}
                        onSignIn={() => setShowLogin(true)}
                      />
                    )}
                    {/* Show login modal if requested */}
                    {showLogin && !pendingVerification && (
                      <div
                        className="modal-overlay open auth-route-overlay"
                        onTransitionEnd={() => {}}
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="auth-route-stage">
                          <LoginForm
                            onLogin={loginUser}
                            onResendVerification={resendVerificationEmail}
                            onClose={() => {
                              setShowLogin(false);
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {/* Show register modal if requested */}
                    {showRegister && !pendingVerification && (
                      <div
                        className="modal-overlay open auth-route-overlay"
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="auth-route-stage">
                          <RegisterForm
                            onRegister={registerUser}
                            onResendVerification={resendVerificationEmail}
                            onClose={() => {
                              setShowRegister(false);
                            }}
                            onLogin={() => {
                              setShowRegister(false);
                              setShowLogin(true);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : showAdminDashboard ? (
                  <>
                    <div style={{ padding: "12px 24px" }}>
                      <button type="button" onClick={() => navigate("/")}>
                        Open workspace
                      </button>
                    </div>
                    <AdminDashboard
                      analytics={analytics}
                      user={user}
                      onLogout={() => setShowLogoutConfirm(true)}
                    />
                  </>
                ) : (
                  // Render full user dashboard for normal users
                  <UserDashboard
                    user={user}
                    content={content}
                    userDefaults={userDefaults}
                    onSaveDefaults={saveUserDefaults}
                    onLogout={() => setShowLogoutConfirm(true)}
                    onUpload={handleContentUpload}
                    mySchedules={mySchedules}
                    mySchedulesLoading={mySchedulesLoading}
                    mySchedulesError={mySchedulesError}
                    onSchedulesChanged={refreshSchedules}
                    scheduleScope={scheduleScope}
                    schedulesLoading={schedulesLoading}
                    hasMoreSchedules={!!scheduleCursor}
                    onScheduleScopeChanged={scope => fetchMySchedules(null, { scope })}
                    onLoadMoreSchedules={() => fetchMySchedules(null, { cursor: scheduleCursor })}
                    onWorkspaceChanged={async workspace => {
                      setActiveWorkspaceId(workspace?.id || null);
                      await fetchUserContent();
                    }}
                    onOpenAdminDashboard={() => navigate("/admin-dashboard")}
                  />
                )}
                {showLogoutConfirm && user && (
                  <LogoutConfirmDialog
                    user={user}
                    isWorking={logoutInProgress}
                    onCancel={() => {
                      if (!logoutInProgress) setShowLogoutConfirm(false);
                    }}
                    onConfirm={confirmLogout}
                  />
                )}
                {/* MFA Modal */}
                {showMfaModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundColor: "rgba(0,0,0,0.5)",
                      zIndex: 10000,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        background: "white",
                        padding: "20px",
                        borderRadius: "8px",
                        maxWidth: "400px",
                        width: "90%",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      }}
                    >
                      <h3 style={{ marginTop: 0 }}>Two-Factor Authentication</h3>
                      <p>Please verify your identity to continue.</p>
                      {mfaError && (
                        <div style={{ color: "red", marginBottom: "10px" }}>{mfaError}</div>
                      )}

                      {!verificationId ? (
                        <div>
                          <p>
                            A verification code will be sent to your phone
                            {mfaResolver?.hints?.[0]?.phoneNumber
                              ? ` ending in ${mfaResolver.hints[0].phoneNumber.slice(-4)}`
                              : ""}
                            .
                          </p>
                          <div id="mfa-recaptcha-container"></div>
                          <div
                            style={{
                              marginTop: "20px",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <button
                              onClick={cancelMfa}
                              style={{ padding: "8px 16px", cursor: "pointer" }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={sendMfaCode}
                              style={{
                                padding: "8px 16px",
                                background: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                            >
                              Send Code
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p>Enter the 6-digit code sent to your phone.</p>
                          <input
                            type="text"
                            value={mfaCode}
                            onChange={e => setMfaCode(e.target.value)}
                            placeholder="123456"
                            style={{
                              width: "100%",
                              padding: "8px",
                              margin: "10px 0",
                              boxSizing: "border-box",
                              fontSize: "16px",
                            }}
                          />
                          <div
                            style={{
                              marginTop: "20px",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <button
                              onClick={cancelMfa}
                              style={{ padding: "8px 16px", cursor: "pointer" }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={verifyMfaCode}
                              style={{
                                padding: "8px 16px",
                                background: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                            >
                              Verify & Sign In
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            }
          />
        </Routes>
      </Suspense>

      {user && <ProductAssistance launcher={false} />}
    </div>
  );
}

export default App;
